import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Alert,
  Typography,
  Tooltip,
  styled,
  Button,
  ButtonProps,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  Divider,
  ListItemText,
} from '@mui/material';
import AppLayout from '../../layouts/AppLayout';
import { fetchData } from '../../utils/apiClient';
import { PERMISSIONS } from '../../constants/permissions';
import IconButton from '@mui/material/IconButton';
import { ArrowBackIosNew } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { purple } from '@mui/material/colors';
import ListItemIcon from '@mui/material/ListItemIcon';
import { LoadingIndicator } from '../../components/LoadingIndicator';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/AccessTimeOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CircularProgress from '@mui/material/CircularProgress';

// Job type definitions
interface Product {
  productUuid: string;
  title: string;
}

interface BatchStatus {
  questionCount: number;
  status: string;
  timestamp: number;
}

interface Summary{
  totalQuestions?: number;
  totalFailedQuestions?: number;
  totalSuccessQuestions?: number;
  totalSkippedQuestions?: number;
  totalDuplicateCount?: number;
  uniqueQuestionsCount?: number;
}

interface StepStatusItem {
  step: string;
  status: string;
  summary?: Summary;
  timestamp: number;
  batches?: Record<string, BatchStatus>;
}

interface Job {
  jobId: string;
  jobType: string;
  status: string;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  details: object;
  product: Product;
}

interface JobSearchResponse {
  job: Job;
  message?: string;
  status?: number;
}

const JobDetailsContainer: React.FC = () => {
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobDetails, setJobDetails] = useState<Job>({} as Job);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stepStatus, setStepStatus] = useState<StepStatusItem[]>([]);
  const [expandedBatches, setExpandedBatches] = useState<boolean>(false);

  // Common function to search job
  const getJobDataApi = async (): Promise<JobSearchResponse> => {
    try {
      const jobId = window.location.pathname.split('/').pop() || '';
      setJobId(jobId)
      const response = await fetchData(
        `/jobs?jobId=${jobId}`,
        PERMISSIONS.GET_JOBS,
      );
      return response;
    } catch (error) {
      console.error('Error fetching job data:', error);
      throw error;
    }
  };

  const fetchJobData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getJobDataApi();
      if (response.job) {
        setJobDetails(response.job);
        if(response.job.details && 'stepStatus' in response.job.details) {
          const stepStatusData = response.job.details['stepStatus'];
          if (Array.isArray(stepStatusData)) {
            setStepStatus(stepStatusData as StepStatusItem[]);
          } else {
            // Handle legacy object format by converting to array
            const stepArray: {
              summary: Summary;
              step: string;
              status: any;
              timestamp: number
            }[] = Object.entries(stepStatusData as Record<string, any>).map(([step, status]) => ({
              step,
              status,
              summary: {
                totalQuestions: 0,
                totalFailedQuestions: 0,
                totalSuccessQuestions: 0,
                totalSkippedQuestions: 0,
                totalDuplicateCount: 0,
                uniqueQuestionsCount: 0
              },
              timestamp: Date.now()
            }));
            setStepStatus(stepArray);
          }
        }
      } else {
        setJobDetails({} as Job);
      }
    } catch (err: any) {
      setError('Failed to fetch job details');
      console.error('Error fetching job details:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // load data on component mount
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchJobData();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [fetchJobData]);

  // Auto-refresh every 30 seconds if job has PENDING status
  useEffect(() => {
    const isPending = jobDetails.status === 'PENDING';
    
    if (isPending && !loading) {
      const refreshInterval = setInterval(() => {
        fetchJobData();
      }, 30000); // 30 seconds
      
      return () => clearInterval(refreshInterval);
    }
  }, [jobDetails.status, loading, fetchJobData]);

  const navigate = useNavigate();
  const navigateToJobDashboardPage = () => {
    navigate('/jobs');
  }

  const ColorButton = styled(Button)<ButtonProps>(({ theme, color }) => ({
    color: theme.palette.getContrastText(purple[500]),
    backgroundColor: color,
  }));

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'FAILED':
        return 'error';
      case 'COMPLETED':
        return 'success';
      case 'PENDING':
        return 'primary';
      default:
        return 'warning';
    }
  };

  const handleStatusIcon = (stepStatus: unknown, overallStatus: string) => {
    // If the overall status is failed and the step status is init, show a circular progress icon
    if (overallStatus === 'FAILED' && stepStatus === 'INIT') {
      return <PendingIcon color="error" />;
    }

    if (overallStatus === 'COMPLETED' && ['PENDING', 'INIT', 'RUNNING'].includes(stepStatus as string)) {
      return <PendingIcon color="warning" />;
    }
    
    switch (stepStatus) {
      case 'SUCCEEDED':
        return <CheckCircleIcon color="success" />;
      case 'SUCCESS':
        return <CheckCircleIcon color="success" />;
      case 'FAILED':
        return <ErrorIcon color="error" />;
      case 'WARNING':
        return <ErrorIcon color="warning" />;
      case 'INIT':
        return <CircularProgress size={20} color="info" />;
      case 'PENDING':
        return <CircularProgress size={20} color="info" />;
      case 'RUNNING':
        return <CircularProgress size={20} color="info" />;
      default:
        return <PendingIcon color="info" />;
    }
  };

  const handleStatusTooltip = (stepStatus: unknown) => {
    switch (stepStatus) {
      case 'SUCCEEDED':
        return 'Completed';
      case 'SUCCESS':
        return 'Completed';
      case 'FAILED':
        return 'Failed';
      case 'WARNING':
        return 'Warning';
      case 'INIT':
        return 'Initialized';
      case 'PENDING':
        return 'Pending';
      case 'RUNNING':
        return 'Running';
      default:
        return 'Pending';
    }
  };

  const handleConvertStepName = (step: string) => {
    switch (step) {
      case 'initiate':
        return 'Initiate Job';

      // Was METADATA Function - Steps
      case 'fetchProductQuestions':
        return 'Fetch Product Metadata From WAS';
      case 'batchProcessStatus':
        return 'Check Process Metadata Status';
      case 'evaluateBatchResults':
        return 'Determine Overall Status';
      case 'refineQuestionCounts':
        return 'Refine question count statistics.';
      case 'validateWASMetadata':
        return 'Validate WAS Product-Question Metadata';
      case 'removeDuplicateQuestions':
        return 'Remove Duplicate Questions from WAS Metadata';

      // SVN Update Function - Steps
      case 'fetchSvnFilesList':
        return 'Fetch SVN Files List';
      case 'processSvnFileContent':
        return 'Process SVN File Content(Zip Extract)';
      case 'mapProcessXMLContent':
        return 'Map Process XML Content';
      case 'cleanSVNFiles':
        return 'Clean SVN Files(Remove Zip File/Save Results Files)';
      case 'updateSVNQuestionCount':
        return 'Update SVN Question Count(Update Database)';
      case 'recordExtractedSVNFiles':
        return 'Record Extracted SVN File Results';

      case 'finalization':
        return 'Finalize';
      default:
        return null;
    }
  };


  return (
    <AppLayout>
      {loading ? (<LoadingIndicator />)
        : (
          <Box sx={{ p: 3 }}>
            {/* Header Section */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography variant="h4" gutterBottom>
                <IconButton onClick={navigateToJobDashboardPage}>
                  <ArrowBackIosNew
                    fontSize={'small'}
                    style={{ marginBottom: '3px', marginRight: '8px' }}
                  />
                </IconButton>
                Job Details - {jobId}
              </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Data */}
            <Box m={2}>
              <Card elevation={2}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1">
                        Job Progress - [ {jobDetails.product.title} ]
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      container
                      direction="column"
                      alignItems="flex-end"
                    >
                      <ColorButton
                        variant="contained"
                        color={getStatusColor(jobDetails.status)}
                        style={{
                          borderRadius: 50,
                          padding: '4px 8px',
                          fontSize: '0.625rem',
                        }}
                        size={'small'}
                      >
                        {jobDetails.status}
                      </ColorButton>
                    </Grid>
                  </Grid>
                  <List>
                    {stepStatus.map((stepItem, index) => (
                      <React.Fragment key={`${stepItem.step}-${index}`}>
                        {stepItem.step === 'batchProcessStatus' && stepItem.batches ? (
                          <>
                            <ListItem>
                              <ListItemIcon>
                                {handleStatusIcon(stepItem.status, jobDetails.status)}
                              </ListItemIcon>
                              <ListItemText 
                                primary={handleConvertStepName(stepItem.step)}
                                secondary="Check the status of each batch processed from WAS metadata"
                              />
                              {stepItem.summary && (
                                <Typography variant="body2" sx={{ ml: 2, fontSize: '0.75rem' }}>
                                  {[
                                    stepItem.summary.totalQuestions != null && `Total Questions: ${stepItem.summary.totalQuestions}`,
                                    stepItem.summary.totalFailedQuestions != null && `Failed Questions: ${stepItem.summary.totalFailedQuestions}`,
                                    stepItem.summary.totalSuccessQuestions != null && `Processed Questions: ${stepItem.summary.totalSuccessQuestions}`,
                                    stepItem.summary.totalSkippedQuestions != null && `Skipped Questions(Root Folder Mismatch): ${stepItem.summary.totalSkippedQuestions}`
                                  ].filter(Boolean).join(' | ')}
                                </Typography>
                              )}
                              <IconButton
                                onClick={() => setExpandedBatches(!expandedBatches)}
                                size="small"
                                sx={{ ml: 1 }}
                              >
                                {expandedBatches ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </ListItem>
                            <Divider />
                            {expandedBatches && Object.entries(stepItem.batches)
                              .sort(([a], [b]) => {
                                const batchNumA = parseInt(a.replace('file_', ''));
                                const batchNumB = parseInt(b.replace('file_', ''));
                                return batchNumA - batchNumB;
                              })
                              .map(([batchKey, batchValue]) => (
                              <React.Fragment key={batchKey}>
                                <Tooltip title={handleStatusTooltip(batchValue.status)}>
                                  <ListItem sx={{ marginLeft: '16px' }}>
                                    <ListItemIcon>
                                      {handleStatusIcon(batchValue.status, jobDetails.status)}
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={`${batchKey} - ${batchValue.questionCount} questions`}
                                    />
                                  </ListItem>
                                </Tooltip>
                                <Divider />
                              </React.Fragment>
                            ))}
                            <Divider />
                          </>
                        ) : (
                          <Tooltip title={handleStatusTooltip(stepItem.status)}>
                            <ListItem>
                              <ListItemIcon>
                                {handleStatusIcon(stepItem.status, jobDetails.status)}
                              </ListItemIcon>
                              <ListItemText
                                primary={handleConvertStepName(stepItem.step)}
                                secondary={stepItem.step === 'mapProcessXMLContent' ? 'Parse and process XML files, update database records accordingly' : null}
                              />
                              {stepItem.summary && (
                                <Typography variant="body2" sx={{ ml: 2, fontSize: '0.75rem' }}>
                                  {[
                                    stepItem.summary.totalQuestions != null && `Total Questions: ${stepItem.summary.totalQuestions}`,
                                    stepItem.summary.totalFailedQuestions != null && `Failed Questions: ${stepItem.summary.totalFailedQuestions}`,
                                    stepItem.summary.totalSuccessQuestions != null && `Successful Questions: ${stepItem.summary.totalSuccessQuestions}`,
                                    stepItem.summary.totalSkippedQuestions != null && `Skipped Questions(Root Folder Mismatch): ${stepItem.summary.totalSkippedQuestions}`,
                                    stepItem.summary.totalDuplicateCount != null && `Duplicate Questions: ${stepItem.summary.totalDuplicateCount}`,
                                    stepItem.summary.uniqueQuestionsCount != null && `Unique Questions: ${stepItem.summary.uniqueQuestionsCount}`
                                  ].filter(Boolean).join(' | ')}
                                </Typography>
                              )}
                            </ListItem>
                          </Tooltip>
                        )}
                        <Divider />
                      </React.Fragment>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Box>

          </Box>
        )
      }
    </AppLayout>
  );
};

export default JobDetailsContainer;
